import { useState, useEffect } from 'react'
import { searchCustomerDisplaySettings } from 'api/core'
import { useCustomerDisplay } from '../context/CustomerDisplayContext'

// Normalize content structure to match expected format
function normalizeContent(items) {
  if (!Array.isArray(items)) return []

  return items.map(item => ({
    ...item,
    // Map API fields to expected fields
    Id: item.CustomerDisplaySettingId || item.Id,
    MediaUrl: item.FileUrl || item.MediaUrl,
    MediaType: item.MediaType?.toLowerCase() || 'image',
    IsActive:
      item.Status !== undefined
        ? item.Status
        : item.IsActive !== undefined
        ? item.IsActive
        : true,
    Order: item.Order,
    CreatedAt: item.StartDate || item.CreatedAt,
  }))
}

// Filter content based on active status, date range, and valid URL
function filterContent(items) {
  if (!Array.isArray(items)) return []

  const now = new Date()

  return items.filter(item => {
    // 1. Only active content
    if (!item.IsActive) return false

    // 2. Validate date range
    if (item.StartDate) {
      const startDate = new Date(item.StartDate)
      if (now < startDate) return false
    }

    if (item.EndDate) {
      const endDate = new Date(item.EndDate)
      if (now > endDate) return false
    }

    // 3. Valid media URL
    if (!item.MediaUrl) return false

    return true
  })
}

// Sort content by Order field (if exists), then by CreatedAt
function sortContent(items) {
  return [...items].sort((a, b) => {
    // Priority: Order field
    if (a.Order !== undefined && b.Order !== undefined) {
      return a.Order - b.Order
    }

    // Fallback: CreatedAt field
    if (a.CreatedAt && b.CreatedAt) {
      return new Date(a.CreatedAt) - new Date(b.CreatedAt)
    }

    return 0
  })
}

export function useScreensaverContent() {
  const [content, setContent] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Use context instead of Redux to get synced values from BroadcastChannel
  const { company: CompanyId, branchOffice: BranchOfficeId } =
    useCustomerDisplay()

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true)
        setError(null)

        const payload = {
          CompanyId,
          BranchOfficeId,
          PageNumber: 1,
          PageSize: 100,
        }

        const response = await searchCustomerDisplaySettings(payload)

        // API returns data in response.data.Data structure
        const rawContent = response?.data?.Data || []

        // Normalize content structure (FileUrl -> MediaUrl, VIDEO -> video)
        const normalizedContent = normalizeContent(rawContent)

        // Filter and sort content
        const filteredContent = filterContent(normalizedContent)

        const sortedContent = sortContent(filteredContent)

        setContent(sortedContent)
      } catch (err) {
        console.error(
          '[useScreensaverContent] Failed to fetch screensaver content:',
          err
        )
        setError(err)
        setContent([]) // Fallback to empty array (will show logo)
      } finally {
        setLoading(false)
      }
    }

    // Only fetch if we have CompanyId and BranchOfficeId
    if (CompanyId && BranchOfficeId) {
      fetchContent()
    } else {
      setLoading(false)
    }
  }, [CompanyId, BranchOfficeId])

  return { content, loading, error }
}
